import React, { useEffect, useState } from "react";
import { Award, Star, Users, Lightbulb, Heart, Rocket, GraduationCap, Trophy, Plus, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Loading, Modal, EmptyState, Avatar, fmtDate } from "../components/ui";

const ICONS = { star: Star, users: Users, lightbulb: Lightbulb, heart: Heart, rocket: Rocket, "graduation-cap": GraduationCap, award: Award };
const RANK = ["#F59E0B", "#94A3B8", "#B45309"];

export default function Recognition() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [feed, setFeed] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ toEmployeeId: "", badge: "", value: "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/recognition/meta"), api.get("/recognition")]).then(([m, f]) => { setMeta(m.data); setFeed(f.data); });
  useEffect(() => { load(); api.get("/employees?limit=200").then(({ data }) => setEmps(data.items)); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/recognition", form); setOpen(false); setForm({ toEmployeeId: "", badge: "", value: "", message: "" }); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!meta || !feed) return <Loading />;

  return (
    <div>
      <PageHeader title="Recognition & Rewards" subtitle="Celebrate great work across SMIFS">
        <button data-testid="give-recognition" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Give Recognition</button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="grid grid-cols-3 gap-4 lg:col-span-2 lg:grid-cols-3">
          {[["My Points", meta.myPoints, "text-amber-500"], ["Received", meta.received, "text-emerald-500"], ["Given", meta.given, "text-blue-500"]].map(([k, v, t]) => (
            <Card key={k} className="p-5" data-testid={`rec-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-slate-500 dark:text-slate-400">{k}</span><Trophy size={16} className={t} /></div>
              <p className="mt-2 font-head text-3xl font-extrabold text-slate-900 dark:text-white">{v}</p>
            </Card>
          ))}
          <Card className="col-span-3 p-5">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Earn a badge for your peers</p>
            <div className="flex flex-wrap gap-3">
              {meta.badges.map((b) => { const I = ICONS[b.icon] || Award; return (
                <div key={b.code} className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 dark:border-slate-700">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: b.color + "22", color: b.color }}><I size={14} /></span>
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{b.label}</span>
                  <span className="text-[10px] text-slate-400">+{b.points}</span>
                </div>
              ); })}
            </div>
          </Card>
        </div>
        <Card className="p-5" data-testid="leaderboard">
          <div className="mb-3 flex items-center gap-2"><Trophy size={16} className="text-amber-500" /><h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Leaderboard</h3></div>
          {meta.leaderboard.length === 0 ? <p className="text-sm text-slate-400">No recognitions yet.</p> : (
            <div className="space-y-2">
              {meta.leaderboard.map((r, i) => (
                <div key={r.employeeId} className="flex items-center gap-3" data-testid={`leader-${i}`}>
                  <span className="w-5 text-center font-head text-sm font-bold" style={{ color: RANK[i] || "#94A3B8" }}>{i + 1}</span>
                  <Avatar name={r.name} size={28} />
                  <span className="flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200">{r.name}</span>
                  <span className="font-mono text-sm font-bold text-amber-600">{r.points}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <h3 className="mb-3 font-head text-lg font-bold text-slate-900 dark:text-white">Recognition Wall</h3>
      {feed.length === 0 ? <EmptyState icon={Award} title="No recognitions yet" hint="Be the first to recognize a colleague." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {feed.map((r) => { const I = ICONS[r.badgeIcon] || Award; return (
            <Card key={r.id} className="p-5" data-testid={`rec-${r.id}`}>
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: r.badgeColor + "22", color: r.badgeColor }}><I size={22} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm"><span className="font-semibold text-slate-800 dark:text-slate-100">{r.fromName}</span> <span className="text-slate-400">recognized</span> <span className="font-semibold text-slate-800 dark:text-slate-100">{r.toName}</span></p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: r.badgeColor + "22", color: r.badgeColor }}>{r.badgeLabel}</span>
                    {r.value && <span className="text-[11px] font-medium text-slate-500">· {r.value}</span>}
                  </div>
                  {r.message && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">"{r.message}"</p>}
                  <p className="mt-2 text-[11px] text-slate-400">{fmtDate(r.createdAt)}</p>
                </div>
              </div>
            </Card>
          ); })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Give Recognition"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="rec-form" disabled={busy || !form.badge} className="btn-accent"><Send size={14} className="mr-1 inline" />Send</button></>}>
        <form id="rec-form" onSubmit={submit} className="space-y-4">
          <div><label className="label">Recognize *</label>
            <select required data-testid="rec-emp" value={form.toEmployeeId} onChange={(e) => setForm({ ...form, toEmployeeId: e.target.value })} className="input">
              <option value="">— Select colleague —</option>{emps.filter((e) => e.id !== user?.employee?.id).map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
            </select>
          </div>
          <div><label className="label">Badge *</label>
            <div className="grid grid-cols-3 gap-2" data-testid="rec-badges">
              {meta.badges.map((b) => { const I = ICONS[b.icon] || Award; const sel = form.badge === b.code; return (
                <button type="button" key={b.code} data-testid={`badge-${b.code}`} onClick={() => setForm({ ...form, badge: b.code })}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-2.5 transition-colors ${sel ? "border-accent bg-accent/5" : "border-slate-200 hover:border-slate-300 dark:border-slate-700"}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: b.color + "22", color: b.color }}><I size={16} /></span>
                  <span className="text-center text-[10px] font-semibold leading-tight text-slate-600 dark:text-slate-300">{b.label}</span>
                </button>
              ); })}
            </div>
          </div>
          <div><label className="label">Company Value</label>
            <select value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="input"><option value="">— Optional —</option>{meta.values.map((v) => <option key={v}>{v}</option>)}</select>
          </div>
          <div><label className="label">Message *</label><textarea required data-testid="rec-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input h-20 py-2" placeholder="What did they do brilliantly?" /></div>
        </form>
      </Modal>
    </div>
  );
}
